import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useEndpointFetch } from '../../../global/network/useEndpointFetch';
import { usePageVisibility } from '../../../global/page-visibility/usePageVisibility';
import { VolunteerCommonProfileDialog } from '../volunteer-common-profile/VolunteerCommonProfileDialog';
import { CommunicationsPagination, PAGE_SIZE_OPTIONS } from './CommunicationsPagination';
import { CommunicationsToolbar } from './CommunicationsToolbar';
import { InboxReplyRow } from './InboxReplyRow';
import { InboxThreadPanel, OpenProfileRequest } from './InboxThreadPanel';
import {
  GetInboxRequest,
  GetInboxResponse,
  InboxReply,
  LookupContactNumberRequest,
  LookupContactNumberResponse,
  CommunicationsPerson,
} from './communications-model';

const POLL_INTERVAL_MS = 30000;

export const InboxScreen = () => {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [receivedFrom, setReceivedFrom] = useState<string | null>(null);
  const [receivedTo, setReceivedTo] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [openProfile, setOpenProfile] = useState<OpenProfileRequest>();
  const [openReply, setOpenReply] = useState<InboxReply>();
  const [replies, setReplies] = useState<InboxReply[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [candidatesByNumber, setCandidatesByNumber] = useState<Record<string, CommunicationsPerson[]>>({});
  const latestRequest = useRef(0);
  const { visibility } = usePageVisibility();

  const getInbox = useEndpointFetch<GetInboxRequest, GetInboxResponse>('/em/comms/inbox');
  const lookupNumber = useEndpointFetch<LookupContactNumberRequest, LookupContactNumberResponse>(
    '/vm/comms/lookup-contact-number'
  );

  const loadCandidates = async (inboundSms: InboxReply[], requestId: number) => {
    const contactNumbers = inboundSms
      .map((reply) => reply.contactNumber)
      .filter((number, index, all) => all.indexOf(number) === index);

    if (contactNumbers.length === 0) {
      setCandidatesByNumber({});
      return;
    }

    const response = await lookupNumber.run({ contactNumbers });
    if (requestId !== latestRequest.current) return;
    setCandidatesByNumber(response && response.ok ? response.data.data?.volunteersByNumber ?? {} : {});
  };

  const loadInbox = useCallback(
    async (silent = false) => {
      latestRequest.current += 1;
      const requestId = latestRequest.current;
      if (!silent) setLoading(true);
      try {
        const response = await getInbox.run({
          search: appliedSearch || undefined,
          receivedFrom: receivedFrom ?? undefined,
          receivedTo: receivedTo ?? undefined,
          offset,
          limit: pageSize,
        });
        if (requestId !== latestRequest.current) return;
        if (response && response.ok && response.data.data) {
          const { inboundSms } = response.data.data;
          setReplies(inboundSms);
          setTotal(response.data.data.pagination.total);
          setOpenReply((open) =>
            open ? inboundSms.find((reply) => reply.inboundSmsId === open.inboundSmsId) ?? open : open
          );
          await loadCandidates(inboundSms, requestId);
        }
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [appliedSearch, receivedFrom, receivedTo, offset, pageSize]
  );

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!visibility) return undefined;
    const timer = setInterval(() => loadInbox(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [visibility, loadInbox]);

  const applySearch = () => {
    setOffset(0);
    setAppliedSearch(search);
  };

  return (
    <Box display='flex' flexDirection='column' flex={1} minHeight={0}>
      <CommunicationsToolbar
        title='Inbox'
        search={search}
        searchPlaceholder='Search replies'
        onSearchChange={setSearch}
        onSearch={applySearch}
        dateRange={{
          from: receivedFrom,
          to: receivedTo,
          label: 'Received',
          onChange: (from, to) => {
            setOffset(0);
            setReceivedFrom(from);
            setReceivedTo(to);
          },
        }}
      />

      <Box flexGrow={1} display='flex' minHeight={0}>
        <Box flexGrow={1} minWidth={0} style={{ overflowY: 'auto' }}>
          {loading && (
            <Box display='flex' justifyContent='center' padding='40px'>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && replies.length === 0 && (
            <Box display='flex' flexDirection='column' alignItems='center' padding='64px 24px'>
              <Typography style={{ fontSize: 16, fontWeight: 600, color: '#444444' }}>No replies yet</Typography>
              <Typography style={{ fontSize: 14, color: '#646464', marginTop: 8, textAlign: 'center', maxWidth: 420 }}>
                When a volunteer replies to an SMS that allows replies, it will show up here.
              </Typography>
            </Box>
          )}

          {!loading &&
            replies.map((reply) => (
              <InboxReplyRow
                key={reply.inboundSmsId}
                reply={reply}
                candidates={candidatesByNumber[reply.contactNumber]}
                onOpen={setOpenReply}
                selected={openReply?.inboundSmsId === reply.inboundSmsId}
              />
            ))}
        </Box>

        {openReply && (
          <InboxThreadPanel
            key={openReply.inboundSmsId}
            reply={openReply}
            candidates={candidatesByNumber[openReply.contactNumber]}
            onClose={() => setOpenReply(undefined)}
            onOpenProfile={setOpenProfile}
          />
        )}
      </Box>

      <CommunicationsPagination
        total={total}
        offset={offset}
        pageSize={pageSize}
        onOffsetChange={setOffset}
        onPageSizeChange={setPageSize}
      />

      {openProfile && (
        <VolunteerCommonProfileDialog
          key={openProfile.profileId}
          profileId={openProfile.profileId}
          userId={openProfile.userId}
          openOn={openProfile.openOn}
          open
          onClose={() => setOpenProfile(undefined)}
        />
      )}
    </Box>
  );
};
